import { useEffect, useRef, useState } from "react";
import type { CSSProperties } from "react";
import { Capacitor } from "@capacitor/core";
import { BrowserMultiFormatReader } from "@zxing/browser";
import type { IScannerControls } from "@zxing/browser";
import { DecodeHintType, BarcodeFormat as ZXFormat } from "@zxing/library";
import {
  BarcodeScanner as Mlkit,
  BarcodeFormat,
} from "@capacitor-mlkit/barcode-scanning";
import type { PluginListenerHandle } from "@capacitor/core";
import { normalizeSeal } from "@routera/shared";
import { getCameraStream } from "../lib/camera";

interface Props {
  onScan: (code: string) => void;
  onClose: () => void;
}

const COOLDOWN_MS = 1500;      // po přijetí ignoruj stejný kód po tuto dobu
const TOAST_MS = 1200;
const CONFIRM_COUNT = 2;       // kolikrát musí přijít stejná hodnota, než ji přijmeme
const CONFIRM_WINDOW_MS = 1500; // hlasy starší než tohle se zahodí (= „už není v záběru")
const MIN_SEAL_LEN = 5;        // kratší = garbage (reálné pečetě ≥6: 846611, D00054075)

// Normalizace PŘED hlasováním (viz `normalizeSeal` v @routera/shared): ML Kit vrací
// v `rawValue` AIM identifikátor symbologie (`]C0846611`), který není součástí pečeti
// a rozbíjel párování s pečetí naskenovanou v počítárně. Normalizujeme na vstupu gate,
// takže se hlasuje i ukládá už jen čistý kód.
//
// Gate proti misreadům: ML Kit/ZXing v continuous režimu pálí událost na KAŽDÝ
// snímek → jeden jednorázový misread (chybí/zaměněné číslo, nebo stray "3"
// z pozadí) se dřív přijal jako další pečeť. Teď kód přijmeme, až když ho
// přečteme STEJNĚ CONFIRM_COUNT× v krátkém okně. Správná hodnota se při držení
// pečeti zopakuje snadno; jednorázový artefakt prahu nedosáhne a zahodí se.
// Vrací přijatý kód (string) nebo null (zatím nepotvrzeno / odmítnuto).
function createScanGate() {
  const votes = new Map<string, { count: number; ts: number }>();
  let accepted: { code: string; ts: number } | null = null;
  return (raw: string | null | undefined): string | null => {
    const code = normalizeSeal(raw);
    if (code.length < MIN_SEAL_LEN) return null;
    const now = Date.now();
    // čerstvě přijatý stejný kód → cooldown, neřeš
    if (accepted && accepted.code === code && now - accepted.ts < COOLDOWN_MS) return null;
    // prune zastaralých hlasů (jiný kód, co se mezitím přestal číst)
    for (const [k, v] of votes) if (now - v.ts > CONFIRM_WINDOW_MS) votes.delete(k);
    const cur = (votes.get(code)?.count ?? 0) + 1;
    votes.set(code, { count: cur, ts: now });
    if (cur < CONFIRM_COUNT) return null;
    votes.delete(code);
    accepted = { code, ts: now };
    return code;
  };
}

// Pečetě jsou 1D čárkáče dvou typů: materiál/sáčky `D00054075` (alfanum) a
// TRANK/depo bedny `846611` (numerické). Omezíme dekódování na běžné lineární
// formáty → rychlejší a bez náhodných QR/2D čtení.
const MLKIT_FORMATS = [
  BarcodeFormat.Code128,
  BarcodeFormat.Code39,
  BarcodeFormat.Code93,
  BarcodeFormat.Itf,
  BarcodeFormat.Codabar,
];
const ZX_FORMATS = [
  ZXFormat.CODE_128,
  ZXFormat.CODE_39,
  ZXFormat.CODE_93,
  ZXFormat.ITF,
  ZXFormat.CODABAR,
];

// ---------------------------------------------------------------------------
// Wrapper — nativně (Android) ML Kit (rychlé), na webu/PWA fallback na ZXing.
// ---------------------------------------------------------------------------
export function BarcodeScanner(props: Props) {
  return Capacitor.isNativePlatform() ? <MlkitScanner {...props} /> : <ZxingScanner {...props} />;
}

// ---------------------------------------------------------------------------
// Nativní ML Kit — continuous (batch) sken, nativní kamera za transparentním
// webview. Zachovává batch UX (skenuješ pečetě za sebou) + baterka.
// ---------------------------------------------------------------------------
function MlkitScanner({ onScan, onClose }: Props) {
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [torch, setTorch] = useState(false);
  const onScanRef = useRef(onScan);
  onScanRef.current = onScan;

  useEffect(() => {
    let listener: PluginListenerHandle | undefined;
    let scanning = false;
    const gate = createScanGate();

    (async () => {
      try {
        const sup = await Mlkit.isSupported();
        if (!sup.supported) { setError("Skener není na tomto zařízení podporován"); return; }
        const perm = await Mlkit.requestPermissions();
        if (perm.camera !== "granted" && perm.camera !== "limited") {
          setError("Kamera není povolená. Povol ji v nastavení aplikace.");
          return;
        }
        listener = await Mlkit.addListener("barcodeScanned", (ev) => {
          const code = gate(ev?.barcode?.rawValue);
          if (!code) return;
          setToast(code);
          window.setTimeout(() => setToast(null), TOAST_MS);
          onScanRef.current(code);
        });
        document.body.classList.add("barcode-scan-active");
        scanning = true;
        await Mlkit.startScan({ formats: MLKIT_FORMATS });
      } catch (e: any) {
        setError(e?.message ?? "Skener nedostupný");
      }
    })();

    return () => {
      document.body.classList.remove("barcode-scan-active");
      if (listener) listener.remove();
      if (scanning) {
        Mlkit.stopScan().catch(() => { /* ignore */ });
        Mlkit.disableTorch().catch(() => { /* ignore */ });
      }
    };
  }, []);

  const toggleTorch = async () => {
    try {
      if (torch) await Mlkit.disableTorch();
      else await Mlkit.enableTorch();
      setTorch((t) => !t);
    } catch { /* ignore */ }
  };

  if (error) {
    return (
      <div style={overlaySolid}>
        <div style={errorBox}>
          <p style={{ color: "#fff", margin: 0 }}>{error}</p>
          <button onClick={onClose} style={closeBtn}>Zavřít</button>
        </div>
      </div>
    );
  }

  return (
    <div className="barcode-scan-overlay" style={overlayTransparent}>
      <div style={frame} />
      {toast && <div style={toastStyle}>✓ {toast}</div>}
      <div style={btnRow}>
        <button onClick={toggleTorch} style={{ ...pillBtn, background: torch ? "rgba(250,204,21,0.85)" : "rgba(255,255,255,0.15)", color: torch ? "#000" : "#fff" }}>
          🔦 {torch ? "Vypnout" : "Baterka"}
        </button>
        <button onClick={onClose} style={{ ...pillBtn, background: "rgba(22,163,74,0.92)", color: "#fff" }}>Hotovo</button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Web fallback — ZXing (JS), omezený jen na lineární formáty pečetí.
// ---------------------------------------------------------------------------
function ZxingScanner({ onScan, onClose }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const controlsRef = useRef<IScannerControls | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    const hints = new Map();
    hints.set(DecodeHintType.POSSIBLE_FORMATS, ZX_FORMATS);
    const reader = new BrowserMultiFormatReader(hints);
    let stopped = false;
    const gate = createScanGate();

    getCameraStream()
      .then((stream) => {
        if (stopped) return;
        const video = videoRef.current!;
        video.srcObject = stream;
        return reader.decodeFromStream(stream, video, (result) => {
          if (!result) return;
          const code = gate(result.getText());
          if (!code) return;
          setToast(code);
          setTimeout(() => setToast(null), TOAST_MS);
          onScan(code);
        });
      })
      .then((controls) => {
        if (controls) controlsRef.current = controls;
      })
      .catch((e: Error) => {
        setError(e?.message ?? "Kamera nedostupná");
      });

    return () => {
      stopped = true;
      if (controlsRef.current) {
        try { controlsRef.current.stop(); } catch { /* ignore */ }
        controlsRef.current = null;
      }
      if (videoRef.current) videoRef.current.srcObject = null;
    };
  }, [onScan]);

  return (
    <div style={overlaySolid}>
      {error ? (
        <div style={errorBox}>
          <p style={{ color: "#fff", margin: 0 }}>{error}</p>
          <button onClick={onClose} style={closeBtn}>Zavřít</button>
        </div>
      ) : (
        <>
          <video ref={videoRef} style={videoStyle} autoPlay playsInline muted />
          <div style={frame} />
          {toast && <div style={toastStyle}>✓ {toast}</div>}
          <button onClick={onClose} style={closeBtn}>Zavřít</button>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Styly
// ---------------------------------------------------------------------------
const overlayBase: CSSProperties = {
  position: "fixed",
  inset: 0,
  zIndex: 9999,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
};
const overlaySolid: CSSProperties = { ...overlayBase, background: "#000" };
const overlayTransparent: CSSProperties = { ...overlayBase, background: "transparent" };

const videoStyle: CSSProperties = {
  width: "100%",
  height: "100%",
  objectFit: "cover",
  position: "absolute",
  inset: 0,
};

const frame: CSSProperties = {
  position: "relative",
  zIndex: 1,
  width: "80%",
  maxWidth: 420,
  height: 110,
  border: "2px solid rgba(255,255,255,0.9)",
  borderRadius: 10,
  boxShadow: "0 0 0 2000px rgba(0,0,0,0.45)",
  pointerEvents: "none",
};

const btnRow: CSSProperties = {
  position: "absolute",
  bottom: 44,
  left: 0,
  right: 0,
  zIndex: 2,
  display: "flex",
  gap: 12,
  justifyContent: "center",
};

const pillBtn: CSSProperties = {
  padding: "14px 28px",
  border: "1.5px solid rgba(255,255,255,0.4)",
  borderRadius: 40,
  fontSize: 16,
  fontWeight: 700,
  cursor: "pointer",
};

const closeBtn: CSSProperties = {
  position: "absolute",
  bottom: 48,
  left: "50%",
  transform: "translateX(-50%)",
  zIndex: 2,
  padding: "14px 36px",
  background: "rgba(255,255,255,0.15)",
  border: "1.5px solid rgba(255,255,255,0.4)",
  borderRadius: 40,
  color: "#fff",
  fontSize: 16,
  fontWeight: 600,
  cursor: "pointer",
};

const errorBox: CSSProperties = {
  padding: 24,
  textAlign: "center",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 16,
};

const toastStyle: CSSProperties = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%) translateY(-90px)",
  zIndex: 3,
  background: "rgba(22,163,74,0.92)",
  color: "#fff",
  padding: "10px 20px",
  borderRadius: 10,
  fontSize: 15,
  fontWeight: 700,
  whiteSpace: "nowrap",
  maxWidth: "85vw",
  overflow: "hidden",
  textOverflow: "ellipsis",
  pointerEvents: "none",
};
